'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const pains = [
  {
    title: 'Founder-led outbound stopped scaling',
    body: 'You are the best seller in the company and the most expensive prospector. Every hour you dial is an hour you are not closing or building.',
  },
  {
    title: 'The cheap-VA route already burned you once',
    body: 'A seat without a system produces activity, not pipeline. The playbook, data, and deliverability were always the missing pieces.',
  },
  {
    title: 'Agencies pitch you a bridge to a hire',
    body: "Their model assumes you will eventually build in-house. Your economics say you never should. Nobody sells the permanent version. We do.",
  },
];

export default function FirstSdrHero() {
  const rightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    const els = rightRef?.current?.querySelectorAll('.rev');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <header
      className="py-24"
      style={{
        background:
          'radial-gradient(900px 500px at 85% -10%, rgba(201,137,65,0.15), transparent 60%), var(--navy-deep)',
      }}
    >
      <div className="max-w-site mx-auto px-7">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-16 items-start">
          <div>
            <div className="inline-flex font-mono text-[12px] tracking-[0.18em] uppercase text-primary border border-primary/50 px-4 py-2 rounded-full mb-6">
              For teams that should never hire an SDR
            </div>
            <h1 className="heading-land text-white">
              Your deal size cannot support a $100K hire
            </h1>
            <p className="mt-5 text-[19px] text-muted-cream leading-relaxed max-w-[56ch]">
              Below roughly $15K in average deal size, the in-house SDR math never closes: the fully
              loaded cost eats the margin the role exists to create. That is not a budgeting failure.
              It is arithmetic. CommandVA is the SDR function your unit economics can actually
              support, permanently.
            </p>
            <div className="flex flex-wrap gap-3.5 mt-9">
              <a
                href="/request-meeting"
                className="inline-flex items-center gap-2.5 font-semibold text-sm px-6 py-3.5 rounded-[10px] bg-primary text-navy-deep border border-transparent transition-all duration-200 hover:bg-gold-bright hover:-translate-y-px hover:shadow-gold"
              >
                Book a strategy call
              </a>
              <Link
                href="/pricing"
                className="inline-flex items-center gap-2.5 font-semibold text-sm px-6 py-3.5 rounded-[10px] border border-line-navy text-white transition-all duration-200 hover:border-primary hover:text-primary"
              >
                See pricing
              </Link>
            </div>
          </div>

          <div ref={rightRef} className="pt-4">
            {pains?.map((p, i) => (
              <div
                key={p?.title}
                className="rev pain-item"
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <b className="block text-[16.5px] mb-1 text-white font-semibold">{p?.title}</b>
                <p className="text-[15px] text-muted-cream leading-relaxed">{p?.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}